import { Link, useParams } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import IncidentCard from '@/components/base/IncidentCard';
import { topics } from '@/mocks/topics';
import { incidentsByTopic } from '@/mocks/incidents';

export default function TopicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const topic = topics.find((item) => item.slug === slug);
  const list = slug ? incidentsByTopic(slug) : [];

  usePageMeta({
    title: topic ? `${topic.name}｜风险分类详解与事件收录 - 黑料雷达` : '分类未找到 - 黑料雷达',
    description: topic
      ? `${topic.summary}${topic.description}`
      : '该风险分类不存在，请返回风险分类总览查看全部类别。',
    keywords: topic ? `${topic.name},网站黑料,安全事件,隐私风险` : '网站黑料,风险分类',
    canonical: topic ? `/topic/${topic.slug}` : '/topics',
    jsonLd: topic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${topic.name} - 网站安全事件`,
            url: `https://worswgu.cn/topic/${topic.slug}`,
            inLanguage: 'zh-CN',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://worswgu.cn/' },
              { '@type': 'ListItem', position: 2, name: '风险分类', item: 'https://worswgu.cn/topics' },
              {
                '@type': 'ListItem',
                position: 3,
                name: topic.name,
                item: `https://worswgu.cn/topic/${topic.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <section className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-price-tag-3-line text-2xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">返回分类总览，或直接前往事件库检索。</p>
        <Link
          to="/topics"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
        >
          返回风险分类
        </Link>
      </section>
    );
  }

  const others = topics.filter((item) => item.slug !== topic.slug);

  return (
    <>
      <section className="relative overflow-hidden border-b border-background-200 bg-foreground-950">
        <div className="absolute inset-0">
          <img
            src={topic.image}
            alt={`${topic.name} 分类背景图`}
            title={`${topic.name} - 风险分类`}
            className="h-full w-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: '风险分类', to: '/topics' },
              { label: topic.name },
            ]}
          />
          <div className="mt-6 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-600 text-background-50">
              <i className={`${topic.icon} text-2xl`}></i>
            </span>
            <h1 className="font-heading text-2xl font-black text-background-50 md:text-4xl">
              {topic.name}
            </h1>
          </div>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-background-200">{topic.description}</p>
          <p className="mt-3 text-xs text-background-300">该分类下已收录 {list.length} 篇事件整理</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px] lg:gap-14">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {topic.name}相关事件
            </h2>
            {list.length > 0 ? (
              <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {list.map((incident) => (
                  <IncidentCard key={incident.id} incident={incident} />
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
                <p className="font-heading text-base font-bold text-foreground-900">
                  这一分类暂时还没有收录事件
                </p>
                <p className="mt-2 text-sm text-foreground-600">我们正在持续补充，可先浏览其他分类。</p>
              </div>
            )}
          </div>

          <aside>
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">其他风险分类</h2>
              <ul className="mt-3 space-y-2.5">
                {others.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/topic/${item.slug}`}
                      className="flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">不确定属于哪一类？</h2>
              <p className="mt-2 text-xs leading-6 text-foreground-600">
                直接在事件库用关键词检索，我们会把相关事件一并列出。
              </p>
              <Link
                to="/incidents"
                className="mt-4 block whitespace-nowrap rounded-md bg-secondary-600 px-4 py-2.5 text-center text-sm text-background-50 transition-colors hover:bg-secondary-700"
              >
                进入事件库
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}